import { Extension } from '@tiptap/core'
import type { Editor } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import type { Node as PMNode } from '@tiptap/pm/model'
import type { PmMatch } from './find-in-editor'

/**
 * Decorates the Find panel's matches in the live editor — every one with `.fr-match`, the current
 * one (Next/Prev's target) additionally with `.fr-match-active` — WITHOUT touching document
 * content. A decoration renders regardless of DOM focus, unlike a native text selection, which is
 * exactly what a Find bar needs: keyboard focus stays in the search input (so typing keeps
 * filtering and Enter keeps advancing) while the match still lights up in the document.
 *
 * Same meta-transaction publish shape as SectionNumbers.ts, and for the same reason: tiptap v3's
 * `Extension.options` is a getter that returns a fresh spread on every access, so an option object
 * cannot be mutated in place after the editor is created — `setFindHighlight()` is the only
 * supported update path.
 */

interface FindHighlightState {
  matches: PmMatch[]
  activeIndex: number
  version: number
}

export const findHighlightKey = new PluginKey<FindHighlightState>('findHighlight')

/** Highlight `matches` (and mark `activeIndex` as the current one), or clear everything with an
 *  empty array. */
export function setFindHighlight(editor: Editor, matches: PmMatch[], activeIndex: number): void {
  if (editor.isDestroyed) return
  editor.view.dispatch(editor.state.tr.setMeta(findHighlightKey, { matches, activeIndex }))
}

export const FindHighlight = Extension.create({
  name: 'findHighlight',

  addProseMirrorPlugins() {
    // Decorations recomputed only when the doc or the published set actually changes.
    let memo: { doc: PMNode; version: number; set: DecorationSet } | null = null

    return [
      new Plugin<FindHighlightState>({
        key: findHighlightKey,

        state: {
          init: () => ({ matches: [], activeIndex: -1, version: 0 }),
          apply: (tr, value) => {
            const published = tr.getMeta(findHighlightKey) as
              | { matches: PmMatch[]; activeIndex: number }
              | undefined
            if (published) {
              return { matches: published.matches, activeIndex: published.activeIndex, version: value.version + 1 }
            }
            // A content change with no accompanying publish (a keystroke, a replace, a remote
            // update landing) invalidates every position this plugin is holding — positions were
            // computed against the doc as it was a moment ago. Drop them rather than risk
            // highlighting the wrong text; the host recomputes and republishes on its own next
            // tick (see components/FindReplace.tsx).
            if (tr.docChanged && value.matches.length > 0) {
              return { matches: [], activeIndex: -1, version: value.version + 1 }
            }
            return value
          },
        },

        props: {
          decorations: (state) => {
            const published = findHighlightKey.getState(state) ?? { matches: [], activeIndex: -1, version: 0 }
            if (memo && memo.doc === state.doc && memo.version === published.version) return memo.set
            const set =
              published.matches.length === 0
                ? DecorationSet.empty
                : DecorationSet.create(
                    state.doc,
                    published.matches.map((m, i) =>
                      Decoration.inline(m.from, m.to, {
                        class: i === published.activeIndex ? 'fr-match fr-match-active' : 'fr-match',
                      }),
                    ),
                  )
            memo = { doc: state.doc, version: published.version, set }
            return set
          },
        },
      }),
    ]
  },
})
